// The lots of one item, in the order stock is drawn (F-CTRL-N73). For every
// tenant: each lot shows what is left and when it expires, a wrongly typed
// date can be corrected, and a lot that has gone off can be written off.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Layers, Pencil, Trash2 } from 'lucide-react'
import apiClient from '@/api/client'
import type { StockItem, StockLot } from '@/types'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { formatDateHuman } from '@/lib/utils'
import { fmtQty } from '@/lib/quantity'
import { DEFAULT_EXPIRY_ALERT_DAYS, expiryLabel, expiryState, expiryTone } from '@/lib/expiry'
import { LOT_CHANGE_QUERY_KEYS, WriteOffLotDialog, type WriteOffTarget } from './WriteOffLotDialog'

type ToastFn = (type: 'success' | 'error', message: string) => void

export function ItemLotsPanel({ item, showToast, windowDays = DEFAULT_EXPIRY_ALERT_DAYS }: {
  item: StockItem
  showToast: ToastFn
  windowDays?: number
}) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    // Under the 'stockItemLots' prefix, so anything that moves stock and
    // invalidates it refreshes this list too.
    queryKey: ['stockItemLots', item.id, 'all'],
    queryFn: () => apiClient.getStockItemLots(item.id, 'all').then((r) => r.data),
  })
  const lots = data?.lots ?? []
  const [editing, setEditing] = useState<{ id: string; expiry: string } | null>(null)
  const [writeOff, setWriteOff] = useState<WriteOffTarget | null>(null)

  const saveExpiry = useMutation({
    mutationFn: (v: { id: string; expiry: string }) => apiClient.updateStockLot(v.id, { expiry_date: v.expiry }),
    onSuccess: () => {
      for (const key of LOT_CHANGE_QUERY_KEYS) qc.invalidateQueries({ queryKey: [key] })
      showToast('success', 'Expiry date updated')
      setEditing(null)
    },
    onError: (e: Error) => showToast('error', e.message || 'Could not update the expiry date'),
  })

  const undated = lots.filter((l) => !l.expiry_date).length

  return (
    <div className="rounded-lg border p-3">
      <h4 className="mb-1 flex items-center gap-1.5 text-sm font-semibold">
        <Layers className="h-4 w-4" /> Stock by expiry
      </h4>
      <p className="mb-2 text-xs text-muted-foreground">
        Each delivery is its own lot. The lot at the top is used first.
      </p>
      {item.tracks_expiry && undated > 0 && (
        <p className="mb-2 rounded-md bg-warning-surface/50 px-3 py-2 text-xs text-warning-fg">
          {undated === 1 ? '1 lot has' : `${undated} lots have`} no expiry date. Set one so it shows in your expiry alerts.
        </p>
      )}
      {isLoading ? (
        <p className="py-3 text-sm text-muted-foreground">Loading…</p>
      ) : lots.length === 0 ? (
        <p className="py-3 text-sm text-muted-foreground">Nothing in stock.</p>
      ) : (
        <ul className="divide-y text-sm">
          {lots.map((l) => (
            <LotRow
              key={l.id}
              lot={l}
              unit={item.unit}
              windowDays={windowDays}
              editing={editing?.id === l.id ? editing.expiry : null}
              saving={saveExpiry.isPending}
              onEdit={() => setEditing({ id: l.id, expiry: l.expiry_date ?? '' })}
              onEditChange={(expiry) => setEditing({ id: l.id, expiry })}
              onCancel={() => setEditing(null)}
              onSave={() => editing && saveExpiry.mutate(editing)}
              onWriteOff={() => setWriteOff({
                lotId: l.id, itemName: item.name, unit: item.unit, quantity: l.quantity_remaining,
                unitCost: l.unit_cost, expiryDate: l.expiry_date, lotNo: l.lot_no,
              })}
              canClear={!item.tracks_expiry}
            />
          ))}
        </ul>
      )}
      <WriteOffLotDialog target={writeOff} onClose={() => setWriteOff(null)} showToast={showToast} />
    </div>
  )
}

function LotRow({ lot, unit, windowDays, editing, saving, onEdit, onEditChange, onCancel, onSave, onWriteOff, canClear }: {
  lot: StockLot
  unit: string
  windowDays: number
  /** The date being typed, or null when this row is not being edited. */
  editing: string | null
  saving: boolean
  onEdit: () => void
  onEditChange: (expiry: string) => void
  onCancel: () => void
  onSave: () => void
  onWriteOff: () => void
  /** An untracked item's lot may go back to having no date. */
  canClear: boolean
}) {
  const state = expiryState(lot.days_until_expiry, windowDays)
  const flagged = state === 'expired' || state === 'today' || state === 'soon'
  const unchanged = editing === (lot.expiry_date ?? '')
  return (
    <li className="py-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium tabular-nums">{fmtQty(lot.quantity_remaining)} {unit}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            {lot.expiry_date ? (
              <span>{state === 'expired' ? 'Expired' : 'Expires'} {formatDateHuman(lot.expiry_date)}</span>
            ) : (
              <span>No expiry date</span>
            )}
            {flagged && (
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-medium ${expiryTone(state).chipCls}`}>
                {expiryLabel(lot.days_until_expiry)}
              </span>
            )}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Received {formatDateHuman(lot.received_at)}
            {lot.lot_no ? ` · lot ${lot.lot_no}` : ''}
            {lot.location_name ? ` · ${lot.location_name}` : ''}
          </p>
        </div>
        {editing === null && (
          <div className="flex shrink-0 items-center gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit}>
              <Pencil className="mr-1 h-3.5 w-3.5" /> {lot.expiry_date ? 'Change date' : 'Set date'}
            </Button>
            <Button variant="ghost" size="sm" className="text-danger-fg hover:text-danger-fg" onClick={onWriteOff}>
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Write off
            </Button>
          </div>
        )}
      </div>
      {editing !== null && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <DatePicker className="w-48" value={editing} onChange={onEditChange} placeholder="Pick the date on the pack" />
          <Button size="sm" disabled={saving || unchanged || (editing === '' && !canClear)} onClick={onSave}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button size="sm" variant="outline" disabled={saving} onClick={onCancel}>Cancel</Button>
        </div>
      )}
    </li>
  )
}
